import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Custom hook that returns the current screen size based on MUI breakpoints
 * @returns {Breakpoint | null} The current breakpoint or null if none matches
 */
const useScreenSize = (): Breakpoint | null => {
  const theme = useTheme();
  
  // Ensure theme and breakpoints are available
  if (!theme || !theme.breakpoints) {
    return null;
  }

  // Check each breakpoint
  const isXs = useMediaQuery(theme.breakpoints.up('xs'));
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));

  // Return the largest matching breakpoint
  if (isXl) return 'xl';
  if (isLg) return 'lg';
  if (isMd) return 'md';
  if (isSm) return 'sm';
  if (isXs) return 'xs';
  
  return null;
};

/**
 * Hook that provides common screen size checks
 * @returns An object with common screen size checks
 */
export const useScreenChecks = () => {
  const theme = useTheme();
  
  // Return default values if theme or breakpoints aren't available
  if (!theme || !theme.breakpoints) {
    return {
      isMobile: false,
      isTablet: false,
      isDesktop: false,
      isLargeScreen: false,
    };
  }
  
  return {
    isMobile: useMediaQuery(theme.breakpoints.down('sm')), // < 600px
    isTablet: useMediaQuery(theme.breakpoints.between('sm', 'lg')), // 600px - 1200px
    isDesktop: useMediaQuery(theme.breakpoints.up('lg')), // > 1200px
    isLargeScreen: useMediaQuery(theme.breakpoints.up('xl')), // > 1536px
  };
};

export default useScreenSize;
